import { useEffect, useState, useCallback } from "react";
import { Loader2, Save, Trash2, Plus, AlertTriangle, Clock, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { fmtHours } from "@/lib/format";

function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}
function localInputToIso(v) {
  if (!v) return "";
  return new Date(v).toISOString();
}

function ShiftRow({ shift, onSaved }) {
  const [pin, setPin] = useState(isoToLocalInput(shift.punch_in));
  const [pout, setPout] = useState(isoToLocalInput(shift.punch_out));
  const [busy, setBusy] = useState(false);
  const open = !shift.punch_out;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/shifts/${shift.id}`, {
        punch_in: localInputToIso(pin),
        punch_out: pout ? localInputToIso(pout) : "",
      });
      toast.success("Entrée corrigée");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer cette entrée de temps ?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/shifts/${shift.id}`);
      toast.success("Entrée supprimée");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#1E293B] bg-[#0B0F17] p-3" data-testid={`shift-row-${shift.id}`}>
      <div className="flex items-center justify-between mb-2">
        {open ? (
          <span className="text-xs font-semibold text-amber-400 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Punch out manquant
          </span>
        ) : (
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {fmtHours(shift.hours)}
          </span>
        )}
        {shift.truck_number && <span className="text-xs text-slate-500">🚚 #{shift.truck_number}</span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-slate-400">Punch In</Label>
          <Input
            type="datetime-local"
            value={pin}
            data-testid={`shift-in-input-${shift.id}`}
            onChange={(e) => setPin(e.target.value)}
            className="mt-1 h-9 bg-[#131C2E] border-[#1E293B] text-sm"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-400">Punch Out</Label>
          <Input
            type="datetime-local"
            value={pout}
            data-testid={`shift-out-input-${shift.id}`}
            onChange={(e) => setPout(e.target.value)}
            className="mt-1 h-9 bg-[#131C2E] border-[#1E293B] text-sm"
          />
        </div>
      </div>
      <div className="flex justify-end gap-1.5 mt-2">
        <Button size="sm" variant="ghost" className="h-8 text-red-400 hover:text-red-300" data-testid={`shift-delete-${shift.id}`} onClick={remove} disabled={busy}>
          <Trash2 className="w-4 h-4" />
        </Button>
        <Button size="sm" className="h-8 bg-[#F59E0B] hover:bg-[#D97706] text-[#0B0F17] font-semibold" data-testid={`shift-save-${shift.id}`} onClick={save} disabled={busy}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Enregistrer</>}
        </Button>
      </div>
    </div>
  );
}

export default function ShiftEditor({ driver, week, open, onOpenChange, onChanged }) {
  const [shifts, setShifts] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newIn, setNewIn] = useState("");
  const [newOut, setNewOut] = useState("");
  const [saving, setSaving] = useState(false);
  const [prime, setPrime] = useState("");
  const [primeNote, setPrimeNote] = useState("");
  const [savingPrime, setSavingPrime] = useState(false);

  const load = useCallback(async () => {
    if (!driver) return;
    setShifts(null);
    const [sh, pr] = await Promise.all([
      api.get(`/admin/drivers/${driver.id}/shifts?week=${week}`),
      api.get(`/admin/primes?driver_id=${driver.id}&week=${week}`),
    ]);
    setShifts(sh.data);
    setPrime(pr.data.amount ? String(pr.data.amount) : "");
    setPrimeNote(pr.data.note || "");
  }, [driver, week]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const refresh = () => { load(); onChanged?.(); };

  const savePrime = async () => {
    setSavingPrime(true);
    try {
      await api.put("/admin/primes", {
        driver_id: driver.id,
        week,
        amount: parseFloat(prime) || 0,
        note: primeNote,
      });
      toast.success("Prime enregistrée");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPrime(false);
    }
  };

  const addEntry = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/shifts", {
        driver_id: driver.id,
        punch_in: localInputToIso(newIn),
        punch_out: localInputToIso(newOut),
      });
      toast.success("Entrée ajoutée");
      setNewIn(""); setNewOut(""); setAdding(false);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131C2E] border-[#1E293B] text-white max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Corriger les heures — {driver?.name}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Modifiez, supprimez ou ajoutez une entrée de temps (utile si un chauffeur oublie son punch out).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Prime (bonus) */}
          <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3" data-testid="prime-editor">
            <div className="flex items-center gap-1.5 mb-2 text-sm font-semibold text-[#F59E0B]">
              <Gift className="w-4 h-4" /> Prime de la semaine
            </div>
            <div className="flex items-end gap-2">
              <div className="w-28">
                <Label className="text-[11px] text-slate-400">Montant ($)</Label>
                <Input
                  type="number" step="0.01" inputMode="decimal"
                  value={prime}
                  data-testid="prime-amount-input"
                  onChange={(e) => setPrime(e.target.value)}
                  placeholder="0.00"
                  className="mt-1 h-9 bg-[#0B0F17] border-[#1E293B] text-sm"
                />
              </div>
              <div className="flex-1">
                <Label className="text-[11px] text-slate-400">Note (optionnel)</Label>
                <Input
                  value={primeNote}
                  data-testid="prime-note-input"
                  onChange={(e) => setPrimeNote(e.target.value)}
                  placeholder="Ex. bonus livraison"
                  className="mt-1 h-9 bg-[#0B0F17] border-[#1E293B] text-sm"
                />
              </div>
              <Button size="sm" className="h-9 bg-[#F59E0B] hover:bg-[#D97706] text-[#0B0F17] font-semibold" data-testid="prime-save-button" onClick={savePrime} disabled={savingPrime}>
                {savingPrime ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> OK</>}
              </Button>
            </div>
          </div>

          {shifts === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
          ) : shifts.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">Aucune entrée cette semaine.</p>
          ) : (
            shifts.map((s) => <ShiftRow key={s.id} shift={s} onSaved={refresh} />)
          )}

          {adding ? (
            <form onSubmit={addEntry} className="rounded-xl border border-dashed border-[#334155] bg-[#0B0F17] p-3 space-y-2" data-testid="add-shift-form">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] text-slate-400">Punch In</Label>
                  <Input type="datetime-local" required value={newIn} data-testid="new-shift-in-input" onChange={(e) => setNewIn(e.target.value)} className="mt-1 h-9 bg-[#131C2E] border-[#1E293B] text-sm" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-400">Punch Out</Label>
                  <Input type="datetime-local" required value={newOut} data-testid="new-shift-out-input" onChange={(e) => setNewOut(e.target.value)} className="mt-1 h-9 bg-[#131C2E] border-[#1E293B] text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-1.5">
                <Button type="button" size="sm" variant="ghost" className="h-8 text-slate-400" onClick={() => setAdding(false)}>Annuler</Button>
                <Button type="submit" size="sm" className="h-8 bg-[#10B981] hover:bg-emerald-600 text-[#0B0F17] font-semibold" data-testid="new-shift-submit" disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
                </Button>
              </div>
            </form>
          ) : (
            <Button variant="outline" className="w-full border-dashed border-[#334155] bg-transparent hover:bg-[#1E293B]" data-testid="add-shift-button" onClick={() => setAdding(true)}>
              <Plus className="w-4 h-4 mr-1" /> Ajouter une entrée manuelle
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
